<template>
  <div id="newsletter">
    <div class="container">

      <TipBox info :title="$t('tips.newsletter.title')">
        <p v-html="$t('tips.newsletter.description')"></p>
        <ul>
          <li>
            <a :href="$t('tips.mailchimp.url')" target="_blank">
              {{ $t('tips.mailchimp.click_here_new') }}
              <svgicon src="/icon/arrow.svg" :height="12" :width="12"></svgicon>
            </a>
          </li>
          <li>
            <a :href="$t('tips.omnisend.url')" target="_blank">
              {{ $t('tips.omnisend.click_here_new') }}
              <svgicon src="/icon/arrow.svg" :height="12" :width="12"></svgicon>
            </a>
          </li>
        </ul>
      </TipBox>

      <Tabs>
        <!-- User Consent section -->
        <TabSection :title="$t('mailchimp.collect_user_consent')" v-if="!companyId">
          <template v-if="!companyId">
            <div class="description">{{ $t('mailchimp.consent_description') }}</div>
            <br>
            <div class="group">
              <input id="chkConsent" type="checkbox" v-model="messagingConsent">
              <label for="chkConsent">{{ $t('mailchimp.consent') }}</label>
            </div>
            <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
              <button class="btn btn-primary" @click="saveConsent">{{ $t('save') }}</button>
            </div>
          </template>
        </TabSection>

        <!-- Omnisend section -->
        <TabSection :title="$t('omnisend.title')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('omnisend.apiKey') }}</label>
                <input type="text" v-model="omnisend.apiKey">
              </div>
              <div class="group">
                <label>{{ $t('omnisend.syncOnSale') }}</label>
                <select v-model="omnisend.syncOnSale">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <br>
              <TipBox warning no-border>
                <span v-html="$t('tips.omnisend.api_key')"></span>
                <a :href="$t('tips.omnisend.api_key_url')" target="_blank">
                  {{ $t('tips.omnisend.click_here') }}
                  <svgicon src="/icon/arrow.svg" :height="12" :width="12"></svgicon>
                </a>
              </TipBox>
              <TipBox info no-border>
                <span>{{ $t('tips.omnisend.api_key_instructions') }}</span>
              </TipBox>
            </div>
          </div>

          <div class="footer-actions">
            <button class="btn btn-primary" @click="saveOmnisend"
              v-if="$store.getters.loggedUserHasModifyPermission">{{ $t("save") }}</button>
          </div>
        </TabSection>

        <!-- Mailchimp section -->
        <TabSection :title="$t('mailchimp.title')">
          <div class="columns">
            <div class="column">
              <TipBox warning no-border>
                <span v-html="$t('tips.mailchimp.api_key')"></span>
                <a :href="$t('tips.mailchimp.api_key_url')" target="_blank">
                  {{ $t('tips.mailchimp.click_here') }}
                  <svgicon src="/icon/arrow.svg" :height="12" :width="12"></svgicon>
                </a>
              </TipBox>
              <div class="group">
                <label>{{ $t('mailchimp.apiKey') }}</label>
                <input type="text" v-model="mailchimp.apiKey" :class="{ invalid: $validate.error('mailchimp.apiKey') }">
              </div>
              <div class="group">
                <label>{{ $t('mailchimp.list') }}</label>
                <select v-model="mailchimp.list.id" :class="{ invalid: $validate.error('mailchimp.list.id') }">
                  <option :value="list.id" v-for="list of accountLists">{{ list.name }}</option>
                </select>
              </div>
              <div class="group">
                <input :id="`chk_update_recent`" type="checkbox" v-model="mailchimp.updateRecentOnly">
                <label :for="`chk_update_recent`">{{ $t('mailchimp.updateRecentOnly') }}</label>
              </div>
              <div class="group">
                <input :id="`chk_auto_update`" type="checkbox" v-model="mailchimp.autoUpdate">
                <label :for="`chk_auto_update`">{{ $t('mailchimp.autoUpdate') }}</label>
              </div>
            </div>
            <div class="column">
              <div class="subtitle">{{ $t('mailchimp.uploaded_fields') }}</div>
              <div class="group" v-for="field of uploadedFields">
                <input :id="`chk_field_${field.id}`" type="checkbox" :value="field.id" v-model="mailchimp.fields">
                <label :for="`chk_field_${field.id}`">{{ $t(field.trans) }}</label>
              </div>
            </div>
          </div>

          <div class="footer-actions">
            <button class="btn btn-primary" @click="saveMailchimp"
              v-if="$store.getters.loggedUserHasModifyPermission">{{ $t("save") }}</button>
            <button class="btn btn-primary" @click="upload">{{ $t("export") }}</button>
          </div>
        </TabSection>
      </Tabs>

    </div>
  </div>
</template>

<script>
  import { Core } from "@/core/Core";
  import { mapActions } from "vuex";
  import EventBus from "../../resources/eventBus";

  export default {
    name: "MailChimp",

    data() {
      return {
        uploadedFields: [
          { trans: 'mailchimp.fields.firstName', id: 'firstName' },
          { trans: 'mailchimp.fields.lastName', id: 'lastName' },
          { trans: 'mailchimp.fields.address', id: 'address' },
          { trans: 'mailchimp.fields.phone', id: 'phone' },
          { trans: 'mailchimp.fields.last_company', id: 'company' },
        ],
        accountLists: [],
        messagingConsent: false,
        mailchimp: {
          apiKey: null,
          list: {
            id: null,
            name: null
          },
          updateRecentOnly: false,
          autoUpdate: false,
          fields: []
        },
        omnisend: {
          apiKey: null,
          syncOnSale: false,
        }
      }
    },

    computed: {
      branchId() {
        return this.$route.query.branch;
      },
      companyId() {
        return this.$route.query.company;
      },
      augment() {
        return 'mailchimp'
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "newsletter.title";
      this.$store.state.pageIcon = null;

      this.$store.commit('showSpinner', true);
      let response = await (
        this.companyId
          ? this.getCompany({ branchId: this.branchId, companyId: this.companyId, augment: this.augment })
          : this.getBranch({ branchId: this.branchId, augment: this.augment })
      );

      if (response) {
        Object.assign(this.mailchimp, response.mailchimp || {});
        Object.assign(this.omnisend, response.omnisend || {});

        if (!this.companyId) {
          //this.messagingConsent = response.messagingConsent;
        }
      }
      EventBus.$emit("setFirstTab");
      this.$store.commit('showSpinner', false);

      if (!this.$store.getters.loggedUserHasModifyPermission) {
        window.lockFields();
      }
    },

    watch: {
      'mailchimp.list.id'(val) {
        let matched = this.accountLists.filter(l => l.id === val)[0];
        this.mailchimp.list.name = matched ? matched.name : ''
      },
      'mailchimp.apiKey': {
        async handler(val) {
          if (!val) {
            return
          }
          if (RegExp('^[a-f0-9]{32}\-').test(val.toLowerCase())) {
            try {
              this.$store.commit('showSpinner', true);
              let response = await Core.request("GET", `/api/mailchimp_lists?apikey=${val}`);
              if (response) {
                this.accountLists = response;
                /* Select first one by default */
                if (this.accountLists.length > 0 && !this.mailchimp.list.id) {
                  this.mailchimp.list.id = this.accountLists[0].id
                }
              }
            } catch (e) {
              console.log(e);
              toast(this.$t("mailchimp.get_lists_error"), "error");
            } finally {
              this.$store.commit('showSpinner', false);
            }
          }
        }
      },
      'omnisend.apiKey': {
        async handler(val) {
          if (!val || val.length !== 75) {
            return;
          }
          try {
            await Core.request("GET", `/api/omnisend/test-key?apikey=${val}`);
            toast(this.$t("omnisend.valid_key"), 'success');
          } catch (e) {
            toast(this.$t("omnisend.invalid_key") + ` Error: ${e.responseText}`, 'error');
          }
        }
      }
    },

    validate: {
      "mailchimp.apiKey": {
        required: true,
        regex: RegExp('^[a-f0-9]{32}\-')
      },
      "mailchimp.list.id": {
        required: true
      },
    },

    methods: {
      ...mapActions(['getCompany', 'saveCompany', 'getBranch', 'saveBranch']),

      async saveMailchimp() {
        if (!this.$validate.valid()) {
          toast(this.$t('invalid_or_missing_fields'), 'error');
          this.$validate.validateAllOnce();
          return false;
        }
        let data = {
          mailchimp: this.mailchimp
        };
        this.$store.commit('showSpinner', true);
        let response = await (
          this.companyId
            ? this.saveCompany({ branchId: this.branchId, companyId: this.companyId, augment: this.augment, data })
            : this.saveBranch({ branchId: this.branchId, augment: this.augment, data })
        );
        this.$store.commit('showSpinner', false);
        if (response) {
          this.mailchimp = response.mailchimp;
          toast(this.$t("mailchimp.save_success"), "success");
          return true;
        } else {
          toast(this.$t("error.no_server"), "error");
          return false;
        }
      },

      async saveOmnisend() {
        let data = {
          omnisend: this.omnisend
        };
        this.$store.commit('showSpinner', true);
        let response = await (
          this.companyId
            ? this.saveCompany({ branchId: this.branchId, companyId: this.companyId, augment: this.augment, data })
            : this.saveBranch({ branchId: this.branchId, augment: this.augment, data })
        );
        this.$store.commit('showSpinner', false);
        if (response) {
          this.omnisend = response.omnisend;
          toast(this.$t("omnisend.save_success"), "success");
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async saveConsent() {
        if (this.companyId) {
          return;
        }
        let data = {
          messagingConsent: this.messagingConsent
        };
        this.$store.commit('showSpinner', true);
        let response = await (this.saveBranch({ branchId: this.branchId, augment: this.augment, data }));
        this.$store.commit('showSpinner', false);
        if (response) {
          toast(this.$t("mailchimp.save_success"), "success");
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async upload() {
        let saveSuccess = await this.saveMailchimp();
        if (!saveSuccess) {
          return;
        }
        let data = {
          branch: this.branchId,
          company: this.companyId,
          next_id: null
        };
        let response = null;
        this.$store.commit("showSpinner", true);
        try {
          do {
            response = await Core.request("PUT", "/api/mailchimp_upload", data);
            data.next_id = response.next_id;
            let progressionMessage = this.$t("mailchimp.upload_progression")
              .replace("%X%", response.success)
              .replace('%Y%', response.invalid)
              .replace("%Z%", response.failed);
            toast(progressionMessage, "success");
          } while (data.next_id);

          toast(this.$t("mailchimp.upload_finished"), "success");
        } catch (e) {
          toast(this.$t("mailchimp.upload_error"), "error");
        }
        this.$store.commit("showSpinner", false);
      }
    }
  }
</script>

<style lang="scss" scoped>
  #newsletter button {
    margin-right: 20px;
    margin-left: 20px;
  }
</style>